import React, { useEffect, useState } from 'react'
import { fetchProjectById, fetchUsers, updateProject } from '../../utils/api/api';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';

const EditProjectForm = ({ onClose }) => {
    const [formData, setFormData] = useState({});
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const { id } = useParams();

    const getProject = async () => {
        setLoading(true);
        try {
            const data = await fetchProjectById(id);
            setFormData(data.project);
            setLoading(false);
        } catch (err) {
            toast.error("Request failed.")
            setLoading(false);
        }
    };    

    const getUsers = async () => {
        try {
            const data = await fetchUsers();
            setUsers(data.users);
        } catch (error) {
            console.error("Failed to fetch users:", error);
        }
    };

    useEffect(() => {
        getProject();
        getUsers();
    }, [id]);

    const handleEditChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData({
                ...formData,
                project_map: file,
            });
        }
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const formDataObj = new FormData();

        formDataObj.append('id', formData.id);
        formDataObj.append('manager_id', formData.manager_id);
        formDataObj.append('title', formData.title);
        formDataObj.append('description', formData.description);
        formDataObj.append('amenities', formData.amenities);
        formDataObj.append('project_type', formData.project_type);
        formDataObj.append('price', formData.price);
        formDataObj.append('deposit', formData.deposit);
        formDataObj.append('installment_price', formData.installment_price);
        formDataObj.append('monthly_installment', formData.monthly_installment);
        formDataObj.append('months', formData.months);
        formDataObj.append('location', formData.location);
        formDataObj.append('size', formData.size);
        formDataObj.append('start_date', formData.start_date);
        formDataObj.append('end_date', formData.end_date);
        formDataObj.append('status', formData.status);
        formDataObj.append('progress', formData.progress);

        if (formData.project_map instanceof File) {
            formDataObj.append('project_map', formData.project_map, formData.project_map.name);
        }

        try {
            await updateProject(formDataObj);
            toast.success("Project updated successfully.");
            onClose();
        } catch (error) {
            toast.error(error.message || "Failed to update project.");
        } finally {
            setLoading(false);
        }
    };

  return (
    <form onSubmit={handleEditSubmit} className="flex flex-col gap-4 text-[13px] font-light">
        <div className="flex flex-col">
            <TextField
                label="Title"
                name="title"
                value={formData.title || ''}
                onChange={handleEditChange}
                required
                variant="outlined"
                size="small"
                InputLabelProps={{
                    shrink: true,
                }}
                fullWidth
            />
        </div>

        <div className="hidden flex-col">
            <TextField
                label="ID"
                name="id"
                value={formData.id || ''}
                onChange={handleEditChange}
                required
                variant="outlined"
                size="small"
                InputLabelProps={{
                    shrink: true,
                }}
                fullWidth
            />
        </div>

        <div className="flex flex-col">
            <TextField
                label="Description"
                name="description"
                value={formData.description || ''}
                onChange={handleEditChange}
                required
                multiline
                rows={4}
                variant="outlined"
                size="small"
                fullWidth
            />
        </div>

        <div className="flex flex-col">
            <TextField
                label="Manager"
                name="manager_id"
                value={formData.manager_id || ''}
                onChange={handleEditChange}
                required
                select
                variant="outlined"
                size="small"
                fullWidth
            >
                <MenuItem value="">
                    <em>Select Manager</em>
                </MenuItem>
                {users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                        {user.first_name} {user.last_name}
                    </MenuItem>
                ))}
            </TextField>
        </div>

        <div className="flex gap-4">
            <div className="flex flex-col w-1/2">
                <TextField
                    label="Price"
                    name="price"
                    type="number"
                    value={formData.price || ''}
                    onChange={handleEditChange}
                    required
                    variant="outlined"
                    size="small"
                    InputLabelProps={{
                        shrink: true,
                    }}
                    fullWidth
                />
            </div>
            <div className="flex flex-col w-1/2">
                <TextField
                    label="Deposit"
                    name="deposit"
                    type="number"
                    value={formData.deposit || ''}
                    onChange={handleEditChange}
                    required
                    variant="outlined"
                    size="small"
                    InputLabelProps={{
                        shrink: true,
                    }}
                    fullWidth
                />
            </div>
        </div>

        <div className="flex gap-4">
            <div className="flex flex-col w-1/2">
                <TextField
                    label="Monthly Installment"
                    name="monthly_installment"
                    type="number"
                    value={formData.monthly_installment || ''}
                    onChange={handleEditChange}
                    required
                    variant="outlined"
                    size="small"
                    InputLabelProps={{
                        shrink: true,
                    }}
                    fullWidth
                />
            </div>
            <div className="flex flex-col w-1/2">
                <TextField
                    label="Months"
                    name="months"
                    type="number"
                    value={formData.months || ''}
                    onChange={handleEditChange}
                    required
                    variant="outlined"
                    size="small"
                    InputLabelProps={{
                        shrink: true,
                    }}
                    fullWidth
                />
            </div>
        </div>

        <div className="flex gap-4">
            <div className="flex flex-col w-1/2">
                <TextField
                    label="Location"
                    name="location"
                    value={formData.location || ''}
                    onChange={handleEditChange}
                    required
                    variant="outlined"
                    size="small"
                    InputLabelProps={{
                        shrink: true,
                    }}
                    fullWidth
                />
            </div>
            <div className="flex flex-col w-1/2">
                <TextField
                    label="Size"
                    name="size"
                    value={formData.size || ''}
                    onChange={handleEditChange}
                    required
                    variant="outlined"
                    size="small"
                    InputLabelProps={{
                        shrink: true,
                    }}
                    fullWidth
                />
            </div>
        </div>

        <div className="flex gap-4">
            <div className="flex flex-col w-1/2">
                <TextField
                    label="Start Date"
                    name="start_date"
                    type="date"
                    value={formData.start_date || ''}
                    onChange={handleEditChange}
                    required
                    variant="outlined"
                    size="small"
                    InputLabelProps={{
                        shrink: true,
                    }}
                    fullWidth
                />
            </div>
            <div className="flex flex-col w-1/2">
                <TextField
                    label="End Date"
                    name="end_date"
                    type="date"
                    value={formData.end_date || ''}
                    onChange={handleEditChange}
                    required
                    variant="outlined"
                    size="small"
                    InputLabelProps={{
                        shrink: true,
                    }}
                    fullWidth
                />
            </div>
        </div>

        <div className="flex flex-col">
            <TextField
                label="Status"
                name="status"
                value={formData.status || ''}
                onChange={handleEditChange}
                required
                select
                variant="outlined"
                size="small"
                fullWidth
            >
                <MenuItem value="not_started">Not Started</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="on_hold">On Hold</MenuItem>
            </TextField>
        </div>

        <div className="flex flex-col w-full">
            <TextField
                label="Installment Price"
                name="installment_price"
                value={formData.installment_price || ''}
                onChange={handleEditChange}
                required
                variant="outlined"
                size="small"
                InputLabelProps={{
                    shrink: true,
                }}
                fullWidth
            />
        </div>

        <div className="flex gap-4">
            <div className="flex flex-col w-1/2">
                <TextField
                    label="Project Type"
                    name="project_type"
                    value={formData.project_type || ''}
                    onChange={handleEditChange}
                    required
                    variant="outlined"
                    size="small"
                    InputLabelProps={{
                        shrink: true,
                    }}
                    fullWidth
                />
            </div>
            <div className="flex flex-col w-1/2">
                <TextField
                    label="Amenities"
                    name="amenities"
                    value={formData.amenities || ''}
                    onChange={handleEditChange}
                    required
                    variant="outlined"
                    size="small"
                    InputLabelProps={{
                        shrink: true,
                    }}
                    placeholder="Comma separated amenities"
                    fullWidth
                />
            </div>
        </div>

        <div className="flex flex-col">
            <TextField
                label="Progress"
                name="progress"
                type="number"
                value={formData.progress || ''}
                onChange={handleEditChange}
                required
                variant="outlined"
                size="small"
                InputLabelProps={{
                    shrink: true,
                }}
                fullWidth
            />
        </div>

        <div className="flex flex-col">
            <TextField
                // label="Project Map"
                name="project_map"
                type="file"
                onChange={handleFileChange}
                required
                variant="outlined"
                size="small"
                fullWidth
            />
        </div>

        <div className="flex justify-end">
            <button
                type="submit"
                disabled={loading}
                className={`bg-secondary text-white px-6 py-1.5 rounded-md ${loading ? 'bg-opacity-50 cursor-not-allowed' : ''}`}
            >
                {loading ? 'Processing...' : 'Update'}
            </button>
        </div>
    </form>

  )
}

export default EditProjectForm